/**
 * The Plant Brief page's decisions that are not words (routes/twin/brief.tsx): where a question for the consultant
 * goes, and which gap a failed read is. Pure.
 */
import { ApiClientError } from "@/lib/api-client";
import type { BriefDriver, BriefUntestable } from "@/lib/api/brief";
import { askHref as consultantAskHref, type CopilotAbout } from "@/lib/copilot/ask";

/**
 * An evidence row's key: its first result, else its place. Never the hypothesis alone: one hypothesis can be tested in
 * several families, each a driver of its own (#1016 review).
 */
export function evidenceKey(d: BriefDriver, index: number): string {
  return d.result_ids[0] ?? `driver-${index}`;
}

/** An untestable row's key: its place (it has no result), with its hypothesis for reading. */
export function untestableKey(u: BriefUntestable, index: number): string {
  return `${u.hypothesis_id}-${index}`;
}

/**
 * Where a person asks the consultant about one piece of evidence: its page, with the question drafted and, where the
 * brief knows it, what the question is about (lib/copilot/ask, which this delegates to).
 */
export function askHref(ask: string, about?: CopilotAbout | null): string {
  return consultantAskHref(ask, about);
}

/** Why there is no brief to show. */
export type BriefGap = "loading" | "no_run" | "unavailable" | "failed";

/**
 * Whether the sidebar shows "Plant brief": where the brief answers, and where no analysis has finished yet (the page
 * says so); never where the platform does not serve the brief, after a failed read, or before the read answers.
 * Without the live API the page shows its sample plant, so the item shows.
 */
export function briefNavVisible(s: { live: boolean; hasData: boolean; error: unknown }): boolean {
  if (!s.live) return true;
  if (s.hasData) return true;
  return s.error ? briefGapOf(s.error) === "no_run" : false;
}

/** Which gap a failed read is: no finished run yet (the engine's own 404), the route not mounted, or a failure. */
export function briefGapOf(err: unknown): BriefGap {
  if (err instanceof ApiClientError && err.status === 404) {
    const body = err.body as { error?: unknown } | null;
    return typeof body === "object" && body !== null && body.error === "brief_not_found" ? "no_run" : "unavailable";
  }
  return "failed";
}
